import { db } from "../firebase";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where
} from "firebase/firestore";

export const LEAVE_TYPES = [
  { value: "annual", label: "Yıllık İzin", affectsBalance: true },
  { value: "excuse", label: "Mazeret İzni", affectsBalance: false },
  { value: "empathy", label: "Empati İzni", affectsBalance: false },
  { value: "medical", label: "Sağlık / Rapor", affectsBalance: false },
  { value: "unpaid", label: "Ücretsiz İzin", affectsBalance: false },
  { value: "marriage", label: "Evlilik İzni", affectsBalance: false },
  { value: "parental", label: "Doğum / Babalık İzni", affectsBalance: false },
  { value: "other", label: "Diğer", affectsBalance: false }
];

export const calculateBusinessDays = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;

  const start = new Date(`${startDate}T12:00:00`);
  const end = new Date(`${endDate}T12:00:00`);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) {
    return 0;
  }

  let count = 0;
  const cursor = new Date(start);

  while (cursor <= end) {
    const day = cursor.getDay();
    if (day !== 0 && day !== 6) count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }

  return count;
};

export const calculateAccruedAnnualLeave = (
  hireDate,
  asOfDate = new Date(),
  annualDays = 14
) => {
  if (!hireDate) return 0;

  const start = new Date(`${hireDate}T12:00:00`);
  const reference = asOfDate instanceof Date
    ? new Date(asOfDate)
    : new Date(`${asOfDate}T12:00:00`);

  if (
    Number.isNaN(start.getTime()) ||
    Number.isNaN(reference.getTime()) ||
    reference < start
  ) {
    return 0;
  }

  let completedYears = reference.getFullYear() - start.getFullYear();
  const anniversary = new Date(
    reference.getFullYear(),
    start.getMonth(),
    start.getDate(),
    12
  );

  if (reference < anniversary) completedYears -= 1;

  return Math.max(0, completedYears) * annualDays;
};

export const getLeaveRequests = async ({ userId, isAdmin }) => {
  const requestsQuery = isAdmin
    ? query(collection(db, "leaveRequests"))
    : query(collection(db, "leaveRequests"), where("employeeId", "==", userId));
  const snapshot = await getDocs(requestsQuery);

  return snapshot.docs
    .map((item) => ({ id: item.id, ...item.data() }))
    .sort((a, b) => {
      const aTime = a.createdAt?.toMillis?.() || 0;
      const bTime = b.createdAt?.toMillis?.() || 0;
      return bTime - aTime;
    });
};

export const createLeaveRequest = async ({
  employeeId,
  employeeName,
  employeeEmail,
  leaveType,
  startDate,
  endDate,
  description,
  attachmentUrl
}) => {
  const totalDays = calculateBusinessDays(startDate, endDate);

  if (!totalDays) {
    throw new Error("İzin tarihleri en az bir iş günü içermelidir.");
  }

  return addDoc(collection(db, "leaveRequests"), {
    employeeId,
    employeeName: employeeName || employeeEmail || "",
    employeeEmail: employeeEmail || "",
    leaveType,
    startDate,
    endDate,
    totalDays,
    description: String(description || "").trim(),
    attachmentUrl: String(attachmentUrl || "").trim(),
    status: "pending",
    reviewNote: "",
    reviewedBy: "",
    reviewedAt: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

export const reviewLeaveRequest = async ({
  requestId,
  status,
  reviewNote,
  reviewerEmail
}) => {
  if (!["approved", "rejected"].includes(status)) {
    throw new Error("Geçersiz izin talebi durumu.");
  }

  return updateDoc(doc(db, "leaveRequests", requestId), {
    status,
    reviewNote: String(reviewNote || "").trim(),
    reviewedBy: reviewerEmail || "",
    reviewedAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

export const cancelLeaveRequest = async (requestId) => {
  const reference = doc(db, "leaveRequests", requestId);
  const snapshot = await getDoc(reference);

  if (!snapshot.exists() || snapshot.data().status !== "pending") {
    throw new Error("Yalnızca bekleyen izin talepleri iptal edilebilir.");
  }

  return updateDoc(reference, {
    status: "cancelled",
    updatedAt: serverTimestamp()
  });
};
